import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminDashboardComponentCreate,
	createAdminDashboardComponent,
	useAdminDashboardComponentTypesQuery,
	useAdminUserViewsQuery,
} from '../../api/admin';

interface Props {
	open: boolean;
	onClose: () => void;
	userId: number;
}

export default function AddDashboardComponentDialog({ open, onClose, userId }: Props) {
	const queryClient = useQueryClient();
	const { data: componentTypes, isLoading: typesLoading } = useAdminDashboardComponentTypesQuery();
	const { data: userViews, isLoading: viewsLoading } = useAdminUserViewsQuery(userId);

	const [type, setType] = useState('');
	const [order, setOrder] = useState<string>('');
	const [size, setSize] = useState<string>('');
	const [viewId, setViewId] = useState<string>('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function resetAndClose() {
		setType('');
		setOrder('');
		setSize('');
		setViewId('');
		setError(null);
		onClose();
	}

	async function handleSubmit() {
		if (!type || order === '') return;
		setSubmitting(true);
		setError(null);
		try {
			const payload: IAdminDashboardComponentCreate = {
				type,
				order: parseInt(order, 10),
				size: size !== '' ? parseInt(size, 10) : null,
				view_id: viewId !== '' ? parseInt(viewId, 10) : null,
			};
			await createAdminDashboardComponent(userId, payload);
			await queryClient.invalidateQueries(['admin', 'users', userId, 'dashboard-components']);
			resetAndClose();
		} catch (e: any) {
			setError(e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}

	const isViewType = type === 'view';
	const canSubmit = !!type && order !== '' && (!isViewType || viewId !== '');

	return (
		<Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundImage: 'none' } }}>
			<DialogTitle>Add Dashboard Component</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
					<FormControl fullWidth size="small" disabled={typesLoading}>
						<InputLabel id="type-label">Type *</InputLabel>
						<Select
							labelId="type-label"
							label="Type *"
							value={type}
							onChange={(e) => {
								setType(e.target.value);
								if (e.target.value !== 'view') setViewId('');
							}}
						>
							{(componentTypes ?? []).map((t) => (
								<MenuItem key={t} value={t}>
									{t}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<TextField
						label="Order *"
						size="small"
						type="number"
						value={order}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrder(e.target.value)}
						inputProps={{ min: 0 }}
					/>

					<FormControl fullWidth size="small">
						<InputLabel id="size-label">Size (optional)</InputLabel>
						<Select
							labelId="size-label"
							label="Size (optional)"
							value={size}
							onChange={(e) => setSize(e.target.value)}
						>
							<MenuItem value=""><em>None</em></MenuItem>
							{[3, 6, 9, 12].map((n) => (
								<MenuItem key={n} value={String(n)}>{n}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth size="small" disabled={!isViewType || viewsLoading}>
						<InputLabel id="view-label">View {isViewType ? '*' : '(only for view type)'}</InputLabel>
						<Select
							labelId="view-label"
							label={`View ${isViewType ? '*' : '(only for view type)'}`}
							value={viewId}
							onChange={(e) => setViewId(e.target.value)}
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{(userViews ?? []).map((v) => (
								<MenuItem key={v.id} value={String(v.id)}>
									{v.name} ({v.display_type})
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{error && <Alert severity="error">{error}</Alert>}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={resetAndClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={!canSubmit || submitting}
					startIcon={submitting ? <CircularProgress size={16} /> : undefined}
				>
					Add
				</Button>
			</DialogActions>
		</Dialog>
	);
}
